import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';

import { Listings } from 'containers/AppContainer/typings';
import {
  CorporealDocument,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Recap/messages';
import { translate } from 'containers/services/translate';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import SuccessDialog from 'components/SuccessDialog';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import {
  ExpertReportType,
  IVehiculeExpertise,
  IVictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import ExpertReport from 'containers/common/LegalProceedings/ExpertReport';
import { Container } from 'containers/LawyerLegalProceedingsPage/ExpertReport/styled-components';
import { useFormatExpertReportState } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/hooks';
import {
  extractExpertReport,
  getDeactivationButton,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/utils';
import { medicalExpertisesValidator } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/form-validations';
import { get, isEmpty } from 'lodash';
import {
  EMPTY_SUPPORT_DOC,
  SUPPORT_DOC_EXPERT_REPORT_ID,
} from 'containers/LawyerRecapPage/constants';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { DOCUMENT_TYPES } from 'constants/global';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { handleOpenDocument } from 'services/utils';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Document from 'components/Document';
import { useSelector } from 'react-redux';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import {
  STATUS_PENDING,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';

export interface LawyerExpertReportProps {
  intl: IntlShape;
  judgementAddDocuments: JudgementADDDocumentType[];
  listings: Listings;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  documentId: string;
  setDocumentId: Function;
  requestCreateExpertiseRaport: Function;
  claimId: number;
  claimNumber: string;
  requestGetExpertiseRaport: Function;
  expertiseReport: ExpertReportType;
  readOnly?: boolean;
  isManager?: boolean;
  judiciaryProcessId: number;
  requestAddExpertiseSupportDocument?: Function;
  requestValidateExpertReport?: Function;
  currentJudiciaryProcess?: any;
  isAssignedLawyer: boolean;
  assignedLawyers: string[];
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  policyInfo: any;
  expertiseRaportFirstInstance: any;
  requestGetDocByDocumentumId: Function;
}

export const LawyerExpertReport: React.FC<LawyerExpertReportProps> = ({
  ...props
}): JSX.Element => {
  const [isSuccessPopinOpen, setIsSuccessPopinOpen] = React.useState<boolean>(
    false,
  );
  const [deactivateButton, setDeactivateButton] = useState([] as any);

  const claimAssignment = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'assignment'], []),
  );

  const lawyerFirmReference = get(
    claimAssignment.find(
      (el: any) =>
        el.assignedLawyer === currentIntervenantStorage.get()?.sagis_code &&
        el.judiciaryProcessId === props.judiciaryProcessId,
    ),
    'firmReference',
    null,
  );
  const [expertReportState, setExpertReportState] = useFormatExpertReportState(
    props.judgementAddDocuments,
    props.expertiseReport,
  );
  const { victimExpertises, vehiculeExpertises } = useMemo(
    () =>
      extractExpertReport(
        expertReportState,

        (v: IVictimExpertise) => !isEmpty(v.victimExpertise),

        (v: IVehiculeExpertise) => !isEmpty(v.vehiculeExpertise),
      ),
    [props.expertiseReport, expertReportState],
  );

  useEffect(() => {
    setDeactivateButton(
      getDeactivationButton(victimExpertises, vehiculeExpertises),
    );
  }, [victimExpertises, vehiculeExpertises]);

  let validateVictimExpertise = {};
  if (victimExpertises[0]) {
    validateVictimExpertise = medicalExpertisesValidator(victimExpertises[0]);
  }

  const handleClick = () => {
    pushTo(PAGES.LAWYER_HOME)();
  };
  React.useEffect(() => {
    props.requestGetExpertiseRaport(
      props.claimId,
      PROCEDURE_TYPES.FIRST_INSTANCE,
      props.judiciaryProcessId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimId, props.judiciaryProcessId]);

  const expertiseSupportDocuments = get(
    props.expertiseReport,
    'supportDocuments',
    [],
  ).filter(
    (el: any) => el.type === DOCUMENT_TYPES.SUPPORT_DOCUMENT_EXPERT_REPORT,
  );

  const [supportDocument, setSupportDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });

  const setExpertiseSupportDocument = (document: CorporealDocument) => {
    if (SUPPORT_DOC_EXPERT_REPORT_ID === props.documentId) {
      setSupportDocument(document);
      props.requestAddExpertiseSupportDocument &&
        props.requestAddExpertiseSupportDocument({
          ...document,
          expertReportId: get(props.expertiseReport, 'id'),
          claimId: props.claimId,
          claimNumber: props.claimNumber,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isLawyer: !props.isManager,
          status: !props.isManager ? STATUS_PENDING : STATUS_VALIDATED,
        });
      props.resetUploadedDocument();
    }
  };
  const creators = [
    ...get(props.expertiseReport, 'victimExpertises', []).map((el: any) => {
      return !isEmpty(get(el, 'creator', ''))
        ? get(el, 'creator', '')
        : get(props.expertiseReport, 'creator', '');
    }),
    ...get(props.expertiseReport, 'vehiculeExpertises', []).map((el: any) => {
      return !isEmpty(get(el, 'creator', ''))
        ? get(el, 'creator', '')
        : get(props.expertiseReport, 'creator', '');
    }),
  ];
  const isCreator = creators.includes(
    currentIntervenantStorage.get()?.preferred_username,
  );
  return (
    <Container id="lawyer-expert-report">
      {(props.isAssignedLawyer || !isEmpty(props.expertiseReport)) && (
        <ExpertReport
          claimId={props.claimId}
          expertReport={props.expertiseReport}
          listings={props.listings}
          documentId={props.documentId}
          setDocumentId={props.setDocumentId}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          resetUploadedDocument={props.resetUploadedDocument}
          expertReportState={expertReportState}
          setExpertReportState={setExpertReportState}
          validateVictimExpertise={validateVictimExpertise}
          isAssignedLawyer={
            !isEmpty(props.expertiseReport) ? isCreator : props.isAssignedLawyer
          }
          isCreator={isCreator}
          deactivateButton={deactivateButton}
          setDeactivateButton={setDeactivateButton}
          judgementAddDocuments={props.judgementAddDocuments}
          setIsSuccessPopinOpen={setIsSuccessPopinOpen}
          victimExpertises={victimExpertises}
          vehicleExpertises={vehiculeExpertises}
          requestCreateExpertiseRaport={props.requestCreateExpertiseRaport}
          requestValidateExpertReport={props.requestValidateExpertReport}
          claimNumber={props.claimNumber}
          requestGetExpertiseRaport={props.requestGetExpertiseRaport}
          judiciaryProcessId={props.judiciaryProcessId}
          assignedLawyers={props.assignedLawyers}
          sagisVictimInfo={props.sagisVictimInfo}
          sagisOpposingParties={props.sagisOpposingParties}
          policyInfo={props.policyInfo}
          imagineDocuments={[]}
          expertiseRaportFirstInstance={props.expertiseRaportFirstInstance}
          requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
        />
      )}
      {featureService.hasFeature(FEATURES.ADD_SUPPORT_DOCUMENTS) &&
        !isEmpty(props.expertiseReport) &&
        isCreator &&
        isEmpty(expertiseSupportDocuments) && (
          <section className="add-recap-bloc">
            {props.resetUploadedDocument &&
              props.uploadDocument &&
              props.uploadedDocument && (
                <>
                  <div className="add-recap-bloc-title">
                    {translate(messages.supportDocument, props)}
                  </div>
                  <UploadDocumentBloc
                    index={1}
                    document={supportDocument}
                    setDocument={setExpertiseSupportDocument}
                    resetUploadedDocument={props.resetUploadedDocument}
                    uploadDocument={props.uploadDocument}
                    uploadedDocument={props.uploadedDocument}
                    docType={DOCUMENT_TYPES.SUPPORT_DOCUMENT_EXPERT_REPORT}
                    setDocumentId={props.setDocumentId}
                    docId={SUPPORT_DOC_EXPERT_REPORT_ID}
                  />
                </>
              )}
          </section>
        )}
      {featureService.hasFeature(FEATURES.ADD_SUPPORT_DOCUMENTS) &&
        !isEmpty(props.expertiseReport) &&
        !isEmpty(expertiseSupportDocuments) && (
          <section className="add-recap-bloc">
            <div className="add-recap-bloc-title">
              {translate(messages.supportDocument, props)}
            </div>
            <div className="support-document-info">
              <Document
                name={get(expertiseSupportDocuments[0], 'name', '')}
                type={get(expertiseSupportDocuments[0], 'type', '')}
                icon={
                  <VisibilityIcon
                    className="consult"
                    onClick={handleOpenDocument(
                      expertiseSupportDocuments[0] as any,
                      props.requestGetDocByDocumentumId,
                    )}
                  />
                }
              />
            </div>
          </section>
        )}
      <SuccessDialog
        title={translate(messages.successDialogTitle, props)}
        secondaryButtonTitle={translate(
          messages.successDialogSecondaryButtonTitle,
          props,
        )}
        open={isSuccessPopinOpen}
        onClose={setIsSuccessPopinOpen}
        homePath={PAGES.LAWYER_HOME}
        isExpertReport={true}
        onPrimaryClick={handleClick}
        isPrimaryButtonDisabled={
          isEmpty(lawyerFirmReference) && !props.isManager
        }
      />
    </Container>
  );
};

export default injectIntl(LawyerExpertReport);
